// Partner (financiero.partner). Financiero → Configuración → Partner.
// Maestro de clientes/proveedores/pacientes/centros de costo (tipo_partner multi).
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { SelectPais } from '@/components/ui/SelectPais';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { Campo, fin, fieldCls } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';

const TIPOS = ['proveedor', 'vendedor', 'empleado', 'supervisor', 'cliente', 'paciente', 'centro_costo'];
const TIPO_IDENT = ['RUC', 'CEDULA', 'PASAPORTE'];
const ESTADOS = ['activo', 'inactivo', 'prospecto', 'bloqueado'];
const SUBTIPOS_PROVEEDOR: { value: string; label: string }[] = [
  { value: 'inventario', label: 'Inventario' },
  { value: 'gastos', label: 'Gastos' },
  { value: 'servicios', label: 'Servicios' },
  { value: 'otros', label: 'Otros' },
];
const SELECT_COLS = 'id,codigo,tipo_partner,razon_social,nombre_comercial,tipo_identificacion,identificacion,email,telefono,celular,sitio_web,industria,direccion,ciudad,provincia,codigo_postal,pais,limite_credito,dias_credito,descuento_general,es_mayorista,estado_partner,notas,subtipo_proveedor,banco_nombre,banco_tipo_cuenta,banco_cuenta';

interface Partner {
  id: string; codigo: string; tipo_partner: string[]; razon_social: string; nombre_comercial: string | null;
  tipo_identificacion: string; identificacion: string; email: string | null; telefono: string | null; celular: string | null;
  sitio_web: string | null; industria: string | null; direccion: string | null; ciudad: string | null; provincia: string | null;
  codigo_postal: string | null; pais: string | null; limite_credito: number; dias_credito: number; descuento_general: number;
  es_mayorista: boolean; estado_partner: string; notas: string | null; subtipo_proveedor: string | null;
  banco_nombre: string | null; banco_tipo_cuenta: string | null; banco_cuenta: string | null;
}
const vacio = {
  codigo: '', tipo_partner: [] as string[], razon_social: '', nombre_comercial: '', tipo_identificacion: 'CEDULA', identificacion: '',
  email: '', telefono: '', celular: '', sitio_web: '', industria: '', direccion: '', ciudad: '', provincia: '', codigo_postal: '',
  pais: 'Ecuador', limite_credito: 0, dias_credito: 0, descuento_general: 0, es_mayorista: false, estado_partner: 'activo', notas: '',
  subtipo_proveedor: 'inventario', banco_nombre: '', banco_tipo_cuenta: '', banco_cuenta: '',
};
type Form = typeof vacio;

export function Partner() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [busqueda, setBusqueda] = useState('');
  const [ftipos, setFtipos] = useState<string[]>([]);   // tipo_partner (varios)
  const [festado, setFestado] = useState('');
  const [fpais, setFpais] = useState('');
  const [fprov, setFprov] = useState('');
  const toggleFtipo = (t: string) => setFtipos((arr) => (arr.includes(t) ? arr.filter((x) => x !== t) : [...arr, t]));
  const { data: rows = [], isFetching } = useQuery<Partner[]>({ queryKey: ['adm-partner'], queryFn: () => fin.list<Partner>('partner', `?select=${SELECT_COLS}&order=razon_social`) });

  const paises = useMemo(() => Array.from(new Set(rows.map((r) => r.pais).filter(Boolean) as string[])).sort(), [rows]);
  const provincias = useMemo(() => Array.from(new Set(rows.map((r) => r.provincia).filter(Boolean) as string[])).sort(), [rows]);

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(vacio);
  const [abierto, setAbierto] = useState(false);
  const set = (k: keyof Form, v: unknown) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTipo = (t: string) => setForm((f) => ({ ...f, tipo_partner: f.tipo_partner.includes(t) ? f.tipo_partner.filter((x) => x !== t) : [...f.tipo_partner, t] }));

  const abrir = (p?: Partner) => {
    if (p) setForm({
      codigo: p.codigo, tipo_partner: p.tipo_partner ?? [], razon_social: p.razon_social, nombre_comercial: p.nombre_comercial ?? '',
      tipo_identificacion: p.tipo_identificacion, identificacion: p.identificacion, email: p.email ?? '', telefono: p.telefono ?? '',
      celular: p.celular ?? '', sitio_web: p.sitio_web ?? '', industria: p.industria ?? '', direccion: p.direccion ?? '', ciudad: p.ciudad ?? '',
      provincia: p.provincia ?? '', codigo_postal: p.codigo_postal ?? '', pais: p.pais ?? 'Ecuador', limite_credito: Number(p.limite_credito ?? 0),
      dias_credito: p.dias_credito ?? 0, descuento_general: Number(p.descuento_general ?? 0), es_mayorista: p.es_mayorista ?? false,
      estado_partner: p.estado_partner, notas: p.notas ?? '', subtipo_proveedor: p.subtipo_proveedor || 'inventario',
      banco_nombre: p.banco_nombre ?? '', banco_tipo_cuenta: p.banco_tipo_cuenta ?? '', banco_cuenta: p.banco_cuenta ?? '',
    });
    else setForm(vacio);
    setEditId(p?.id ?? null); setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => {
      const body = {
        codigo: form.codigo, tipo_partner: form.tipo_partner, razon_social: form.razon_social, nombre_comercial: form.nombre_comercial || null,
        tipo_identificacion: form.tipo_identificacion, identificacion: form.identificacion, email: form.email || null, telefono: form.telefono || null,
        celular: form.celular || null, sitio_web: form.sitio_web || null, industria: form.industria || null, direccion: form.direccion || null,
        ciudad: form.ciudad || null, provincia: form.provincia || null, codigo_postal: form.codigo_postal || null, pais: form.pais || null,
        limite_credito: form.limite_credito, dias_credito: form.dias_credito, descuento_general: form.descuento_general,
        es_mayorista: form.es_mayorista, estado_partner: form.estado_partner,
        notas: form.notas || null, subtipo_proveedor: form.subtipo_proveedor || 'inventario',
        banco_nombre: form.banco_nombre || null, banco_tipo_cuenta: form.banco_tipo_cuenta || null,
        banco_cuenta: form.banco_cuenta || null, updated_at: new Date().toISOString(),
      };
      return editId ? fin.update('partner', editId, body) : fin.create('partner', body);
    },
    onSuccess: () => { toast.success('Partner guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['adm-partner'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => fin.remove('partner', id),
    onSuccess: () => { toast.success('Partner eliminado'); void qc.invalidateQueries({ queryKey: ['adm-partner'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const filtradas = useMemo(() => rows.filter((p) => {
    if (busqueda && !`${p.razon_social} ${p.identificacion} ${p.codigo}`.toLowerCase().includes(busqueda.toLowerCase())) return false;
    if (ftipos.length && !(p.tipo_partner ?? []).some((t) => ftipos.includes(t))) return false;
    if (festado && p.estado_partner !== festado) return false;
    if (fpais && p.pais !== fpais) return false;
    if (fprov && p.provincia !== fprov) return false;
    return true;
  }), [rows, busqueda, ftipos, festado, fpais, fprov]);

  const hayFiltros = !!(busqueda || ftipos.length || festado || fpais || fprov);
  const limpiar = () => { setBusqueda(''); setFtipos([]); setFestado(''); setFpais(''); setFprov(''); };

  // Exportar (XLS / PDF / CSV) la tabla filtrada
  const expHeaders = ['N°', 'Código', 'Razón social', 'Nombre comercial', 'Identificación', 'Tipo ident.', 'Tipos', 'Estado', 'Email', 'Teléfono', 'Celular', 'Ciudad', 'Provincia', 'País'];
  const expFilas: Celda[][] = filtradas.map((p, i): Celda[] => [
    i + 1, p.codigo, p.razon_social, p.nombre_comercial ?? '', p.identificacion, p.tipo_identificacion,
    (p.tipo_partner ?? []).join(', '), p.estado_partner, p.email ?? '', p.telefono ?? '', p.celular ?? '',
    p.ciudad ?? '', p.provincia ?? '', p.pais ?? '',
  ]);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      {abierto ? (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setAbierto(false)}><ArrowLeft className="w-4 h-4 mr-1" /> Volver</Button>
          <h1 className="text-xl font-semibold">{editId ? 'Editar partner' : 'Nuevo partner'}</h1>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">Partner</h1>
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo partner</Button>
        </div>
      )}
      {!abierto && (<>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <input className={`${fieldCls} pl-9`} placeholder="Buscar por nombre, identificación o código…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
      </div>

      <Card>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4">
          <Campo label="Tipo de partner (varios)">
            <details className="relative">
              <summary className={`${fieldCls} flex items-center cursor-pointer list-none [&::-webkit-details-marker]:hidden`}>
                {ftipos.length ? `${ftipos.length} seleccionado(s)` : '— Todos —'}
              </summary>
              <div className="absolute z-20 mt-1 w-56 space-y-1 rounded-md border bg-card p-2 shadow-lg">
                {TIPOS.map((t) => (
                  <label key={t} className="flex items-center gap-2 text-sm capitalize cursor-pointer select-none">
                    <input type="checkbox" className="h-4 w-4 rounded border-input" checked={ftipos.includes(t)} onChange={() => toggleFtipo(t)} />
                    {t.replace('_', ' ')}
                  </label>
                ))}
              </div>
            </details>
          </Campo>
          <Campo label="Estado">
            <select className={fieldCls} value={festado} onChange={(e) => setFestado(e.target.value)}>
              <option value="">— Todos —</option>
              {ESTADOS.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
            </select>
          </Campo>
          <Campo label="País">
            <select className={fieldCls} value={fpais} onChange={(e) => setFpais(e.target.value)}>
              <option value="">— Todos —</option>
              {paises.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Campo>
          <Campo label="Provincia">
            <select className={fieldCls} value={fprov} onChange={(e) => setFprov(e.target.value)}>
              <option value="">— Todas —</option>
              {provincias.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Campo>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          <div className="mb-2 flex items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">{filtradas.length} resultado(s)</span>
            <div className="flex items-center gap-3">
              {hayFiltros && <button className="text-xs text-primary hover:underline" onClick={limpiar}>Limpiar filtros</button>}
              <ExportarBotones nombre={`partners-${fechaLocal()}`} titulo="Partners"
                subtitulo={`${filtradas.length} registro(s)`} headers={expHeaders} filas={expFilas} colsDerecha={[0]} />
            </div>
          </div>
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Razón social</th><th className="py-2 pr-3">Identificación</th><th className="py-2 pr-3">Tipos</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filtradas.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin partners</td></tr>}
              {filtradas.map((p) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{p.codigo}</td>
                  <td className="py-2 pr-3 font-medium">{p.razon_social}</td>
                  <td className="py-2 pr-3 font-mono">{p.identificacion}</td>
                  <td className="py-2 pr-3 text-xs">{(p.tipo_partner ?? []).join(', ')}</td>
                  <td className="py-2 pr-3 capitalize">{p.estado_partner}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(p)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${p.razon_social}"?`, { peligro: true })) eliminar.mutate(p.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </>)}

      {abierto && (
        <Card>
          <CardContent className="pt-4">
          <div className="space-y-4">
            <Sec titulo="Identificación">
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Código *"><input className={fieldCls} maxLength={20} value={form.codigo} onChange={(e) => set('codigo', e.target.value)} /></Campo>
                <Campo label="Razón social *"><input className={fieldCls} maxLength={300} value={form.razon_social} onChange={(e) => set('razon_social', e.target.value)} /></Campo>
                <Campo label="Nombre comercial"><input className={fieldCls} value={form.nombre_comercial} onChange={(e) => set('nombre_comercial', e.target.value)} /></Campo>
                <div />
                <Campo label="Tipo de identificación">
                  <select className={fieldCls} value={form.tipo_identificacion} onChange={(e) => set('tipo_identificacion', e.target.value)}>{TIPO_IDENT.map((t) => <option key={t} value={t}>{t}</option>)}</select>
                </Campo>
                <Campo label="Identificación *"><input className={fieldCls} maxLength={13} value={form.identificacion} onChange={(e) => set('identificacion', e.target.value)} /></Campo>
              </div>
              <Campo label="Tipo de partner (uno o varios)">
                <div className="flex flex-wrap gap-3 pt-1">
                  {TIPOS.map((t) => {
                    // Los pacientes se crean/gestionan desde Centro Médico: si el partner ya es
                    // paciente, todo el campo es de solo lectura; al crear uno nuevo, 'paciente'
                    // no se puede marcar aquí.
                    const esPacienteExistente = !!editId && form.tipo_partner.includes('paciente');
                    const bloqueado = esPacienteExistente || (t === 'paciente' && !editId);
                    return (
                      <label key={t} className={`flex items-center gap-1.5 text-sm capitalize ${bloqueado ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                        <input type="checkbox" className="h-4 w-4" disabled={bloqueado} checked={form.tipo_partner.includes(t)} onChange={() => toggleTipo(t)} /> {t.replace('_', ' ')}
                      </label>
                    );
                  })}
                </div>
                {form.tipo_partner.includes('paciente') && (
                  <p className="pt-1.5 text-xs text-muted-foreground">Tipo de solo lectura: los pacientes se crean y gestionan desde <b>Centro Médico</b>.</p>
                )}
              </Campo>
              {form.tipo_partner.includes('proveedor') && (
                <Campo label="Subtipo de proveedor">
                  <select className={fieldCls} value={form.subtipo_proveedor} onChange={(e) => set('subtipo_proveedor', e.target.value)}>
                    {SUBTIPOS_PROVEEDOR.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <p className="pt-1 text-xs text-muted-foreground">Solo los proveedores de subtipo &quot;Inventario&quot; aparecen para escoger en la Recepción.</p>
                </Campo>
              )}
            </Sec>

            <Sec titulo="Contacto">
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Email"><input type="email" className={fieldCls} value={form.email} onChange={(e) => set('email', e.target.value)} /></Campo>
                <Campo label="Sitio web"><input className={fieldCls} value={form.sitio_web} onChange={(e) => set('sitio_web', e.target.value)} /></Campo>
                <Campo label="Teléfono"><input className={fieldCls} value={form.telefono} onChange={(e) => set('telefono', e.target.value)} /></Campo>
                <Campo label="Celular"><input className={fieldCls} value={form.celular} onChange={(e) => set('celular', e.target.value)} /></Campo>
                <Campo label="Industria" className="col-span-2"><input className={fieldCls} value={form.industria} onChange={(e) => set('industria', e.target.value)} /></Campo>
              </div>
            </Sec>

            <Sec titulo="Ubicación">
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Dirección" className="col-span-2"><input className={fieldCls} value={form.direccion} onChange={(e) => set('direccion', e.target.value)} /></Campo>
                <Campo label="Ciudad"><input className={fieldCls} value={form.ciudad} onChange={(e) => set('ciudad', e.target.value)} /></Campo>
                <Campo label="Provincia"><input className={fieldCls} value={form.provincia} onChange={(e) => set('provincia', e.target.value)} /></Campo>
                <Campo label="Código postal"><input className={fieldCls} value={form.codigo_postal} onChange={(e) => set('codigo_postal', e.target.value)} /></Campo>
                <Campo label="País"><SelectPais className={fieldCls} value={form.pais} onChange={(v) => set('pais', v)} /></Campo>
              </div>
            </Sec>

            {/* Sin estos datos el proveedor no puede entrar en un pago por lote:
                el banco necesita banco, tipo y número de cuenta para acreditar. */}
            <Sec titulo="Datos bancarios (para pagos por lote)">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Campo label="Banco"><input className={fieldCls} value={form.banco_nombre} maxLength={80}
                  onChange={(e) => set('banco_nombre', e.target.value)} placeholder="Banco Pichincha" /></Campo>
                <Campo label="Tipo de cuenta">
                  <select className={fieldCls} value={form.banco_tipo_cuenta} onChange={(e) => set('banco_tipo_cuenta', e.target.value)}>
                    <option value="">— Sin definir —</option>
                    <option value="ahorros">Ahorros</option>
                    <option value="corriente">Corriente</option>
                  </select>
                </Campo>
                <Campo label="N.º de cuenta"><input className={`${fieldCls} font-mono`} value={form.banco_cuenta} maxLength={30}
                  onChange={(e) => set('banco_cuenta', e.target.value)} /></Campo>
              </div>
            </Sec>

            <Sec titulo="Comercial">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Campo label="Límite de crédito"><input type="number" step="0.01" className={fieldCls} value={form.limite_credito} onChange={(e) => set('limite_credito', Number(e.target.value))} /></Campo>
                <Campo label="Días de crédito"><input type="number" className={fieldCls} value={form.dias_credito} onChange={(e) => set('dias_credito', Number(e.target.value))} /></Campo>
                <Campo label="Descuento (%)"><input type="number" step="0.01" className={fieldCls} value={form.descuento_general} onChange={(e) => set('descuento_general', Number(e.target.value))} /></Campo>
                <Campo label="Estado"><select className={fieldCls} value={form.estado_partner} onChange={(e) => set('estado_partner', e.target.value)}>{ESTADOS.map((s) => <option key={s} value={s}>{s}</option>)}</select></Campo>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="h-4 w-4" checked={form.es_mayorista} onChange={(e) => set('es_mayorista', e.target.checked)} />
                <span className="font-medium">Cliente mayorista</span>
                <span className="text-xs text-muted-foreground">— su Descuento (%) siempre manda e ignora los descuentos del catálogo</span>
              </label>
              <Campo label="Notas"><textarea className={`${fieldCls} h-16`} value={form.notas} onChange={(e) => set('notas', e.target.value)} /></Campo>
            </Sec>

            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.codigo.trim() || !form.razon_social.trim() || !form.identificacion.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Sec({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return <section className="space-y-2"><h4 className="text-sm font-semibold text-muted-foreground">{titulo}</h4>{children}</section>;
}
